import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Box, Button, Tooltip} from "@mantine/core";
import cx from "clsx";
import protocolClasses from "../ProtocolRecorder.module.css";
import {CountdownState, formatCountdownDisplay} from "./CountdownDisplay";

interface FloatingCountdownPanelProps {
    countDownState: CountdownState;
    onStop: (state: CountdownState) => void;
    onCancel: () => void;
}

interface TPoint {
    x: number;
    y: number;
}

const clamp = (value: number, min: number, max: number): number => {
    if (value < min) return min;
    if (value > max) return max;
    return value;
};

/**
 * Floating draggable mini timer shown outside focus mode while countdown is active.
 */
export const FloatingCountdownPanel: React.FC<FloatingCountdownPanelProps> = ({
    countDownState,
    onStop,
    onCancel,
}) => {
    const [position, setPosition] = useState<TPoint>({x: 0, y: 0});
    const panelRef = useRef<HTMLDivElement>(null);
    const dragStateRef = useRef<{
        pointerId: number;
        pointerStart: TPoint;
        panelStart: TPoint;
    } | null>(null);
    const bodyUserSelectRef = useRef<string>("");
    const bodyWebkitUserSelectRef = useRef<string>("");
    const bodyCursorRef = useRef<string>("");

    const setDraggingSelectionDisabled = (isDragging: boolean) => {
        if (typeof document === "undefined") return;
        if (isDragging) {
            bodyUserSelectRef.current = document.body.style.userSelect;
            bodyWebkitUserSelectRef.current = document.body.style.webkitUserSelect;
            bodyCursorRef.current = document.body.style.cursor;
            document.body.style.userSelect = "none";
            document.body.style.webkitUserSelect = "none";
            document.body.style.cursor = "grabbing";
        } else {
            document.body.style.userSelect = bodyUserSelectRef.current;
            document.body.style.webkitUserSelect = bodyWebkitUserSelectRef.current;
            document.body.style.cursor = bodyCursorRef.current;
        }
    };

    useEffect(() => {
        return () => {
            setDraggingSelectionDisabled(false);
        };
    }, []);

    useLayoutEffect(() => {
        const margin = 16;
        const width = panelRef.current?.offsetWidth ?? 0;
        const height = panelRef.current?.offsetHeight ?? 0;
        setPosition({
            x: Math.max(0, window.innerWidth - width - margin),
            y: Math.max(0, window.innerHeight - height - margin),
        });
    }, []);

    const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        setDraggingSelectionDisabled(true);
        dragStateRef.current = {
            pointerId: event.pointerId,
            pointerStart: {x: event.clientX, y: event.clientY},
            panelStart: position,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handlePointerMove: React.PointerEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        const dragState = dragStateRef.current;
        if (!dragState || dragState.pointerId !== event.pointerId) {
            return;
        }

        const width = panelRef.current?.offsetWidth ?? 0;
        const height = panelRef.current?.offsetHeight ?? 0;
        const nextX = dragState.panelStart.x + (event.clientX - dragState.pointerStart.x);
        const nextY = dragState.panelStart.y + (event.clientY - dragState.pointerStart.y);

        setPosition({
            x: clamp(nextX, 0, Math.max(0, window.innerWidth - width)),
            y: clamp(nextY, 0, Math.max(0, window.innerHeight - height)),
        });
    };

    const handlePointerUp: React.PointerEventHandler<HTMLDivElement> = (event) => {
        event.preventDefault();
        const dragState = dragStateRef.current;
        if (!dragState || dragState.pointerId !== event.pointerId) {
            return;
        }
        dragStateRef.current = null;
        setDraggingSelectionDisabled(false);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };

    return (
        <Box
            ref={panelRef}
            data-testid="floating-countdown-panel"
            className={cx(protocolClasses.floatingCountdownPanel)}
            style={{left: `${position.x}px`, top: `${position.y}px`}}
        >
            <Box
                className={cx(protocolClasses.floatingCountdownHandle)}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
            />
            <Box className={cx(protocolClasses.floatingCountdownContent)}>
                <Box className={cx(protocolClasses.floatingCountdownTitle)}>{countDownState.description}</Box>
                <Box className={cx(protocolClasses.floatingCountdownText)}>
                    {formatCountdownDisplay(countDownState)}
                </Box>
                <Box className={cx(protocolClasses.floatingCountdownButtons)}>
                    <Tooltip label={"Cancel the activity."}>
                        <Button bg={"gray"} size={"xs"} onClick={onCancel}>
                            Cancel
                        </Button>
                    </Tooltip>
                    <Box/>
                    <Tooltip label={"End the activity early."}>
                        <Button bg={"red"} size={"xs"} onClick={() => onStop(countDownState)}>
                            Stop
                        </Button>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
};
